import { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ArrowUpRight } from "lucide-react";
import { LogoLight } from "./Logo";
import { PRODUCTS } from "@/data/products";

const underline = (active) =>
  `relative py-1 after:absolute after:left-0 after:right-0 after:-bottom-0.5 after:h-[2px] after:rounded-full after:bg-[#0B14A0] after:origin-left after:transition-transform after:duration-300 ${active ? "after:scale-x-100 text-[#0B14A0]" : "after:scale-x-0 hover:after:scale-x-100 text-[#1C2150]"}`;

const linkCls = ({ isActive }) =>
  `${underline(isActive)} text-sm sm:text-[15px] font-medium transition-colors duration-300 hover:text-[#0B14A0]`;

const ProductsMenu = () => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const { pathname } = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, []);

  const shown = open ? "visible opacity-100 translate-y-0" : "invisible opacity-0 translate-y-2";
  return (
  <div ref={ref} className="group relative" onPointerEnter={(e) => e.pointerType === "mouse" && setOpen(true)} onPointerLeave={(e) => e.pointerType === "mouse" && setOpen(false)}>
    <button data-active={pathname.startsWith("/produkter")} onClick={(e) => e.detail === 0 || !window.matchMedia("(hover: hover)").matches ? setOpen((o) => !o) : setOpen(true)} aria-expanded={open} data-testid="nav-link-produkter" className={`${underline(pathname.startsWith("/produkter"))} flex items-center gap-1 text-sm sm:text-[15px] font-medium hover:text-[#0B14A0] transition-colors`}>
      Produkter <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
    </button>
    <div className={`${shown} transition-[opacity,transform,visibility] duration-300 absolute left-0 md:left-1/2 md:-translate-x-1/2 pt-4 z-50`} data-testid="nav-products-dropdown">
      <div className="w-[min(20rem,calc(100vw-3rem))] rounded-2xl bg-white border border-[#E3E2DA] shadow-[0_30px_60px_-20px_rgba(7,11,46,0.25)] p-2">
        {PRODUCTS.map((p) => (
          <Link key={p.slug} to={`/produkter/${p.slug}`} data-testid={`nav-dropdown-${p.slug}`} className="flex items-start justify-between gap-3 rounded-xl px-4 py-3 hover:bg-[#F3F2EC] transition-colors">
            <span>
              <span className="block font-semibold text-[#070B2E]">{p.short}</span>
              <span className="block text-sm text-[#4A4F6E] line-clamp-1">{p.intro}</span>
            </span>
            <ArrowUpRight className="w-4 h-4 mt-1 text-[#0B14A0] shrink-0" />
          </Link>
        ))}
      </div>
    </div>
  </div>
  );
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const { pathname, hash } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`sticky top-0 z-50 transition-[background-color,box-shadow] duration-500 ${scrolled ? "bg-white/90 backdrop-blur-xl shadow-[0_1px_0_rgba(7,11,46,0.08)]" : "bg-[#F3F2EC]/0"}`} data-testid="site-header">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-3 md:py-0 md:h-20 flex flex-wrap md:flex-nowrap items-center justify-between gap-y-3">
        <Link to="/" data-testid="nav-brand-logo" className="shrink-0">
          <LogoLight className="h-7 sm:h-8" />
        </Link>
        <nav className="order-last md:order-none w-full md:w-auto flex items-center justify-between md:justify-center gap-4 md:gap-7 lg:gap-9 border-t border-[#070B2E]/10 md:border-0 pt-3 md:pt-0" data-testid="main-nav">
          <ProductsMenu />
          <NavLink to="/#tjanster" className={() => linkCls({ isActive: pathname === "/" && hash === "#tjanster" })} data-testid="nav-link-tjanster">Tjänster</NavLink>
          <NavLink to="/nyheter" className={linkCls} data-testid="nav-link-nyheter">Nyheter</NavLink>
          <NavLink to="/kontakt" className={linkCls} data-testid="nav-link-kontakt">Kontakt</NavLink>
        </nav>
        <div className="flex items-center gap-3">
          <Link to="/kontakt?subject=Boka%20demo" data-testid="nav-cta-boka-demo" className="inline-flex items-center gap-2 rounded-full bg-[#070B2E] text-white pl-5 pr-4 py-2.5 text-sm font-semibold transition-[background-color,transform] duration-300 hover:bg-[#0B14A0] hover:-translate-y-0.5">
            Boka demo <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </header>
  );
};
